<h2 mat-dialog-title>Instruktøranmodninger</h2>
<mat-dialog-content>
  @if (loading) {
  <div class="loading"><mat-spinner diameter="40"></mat-spinner></div>
  } @if (error) {
  <p class="error">{{ error }}</p>
  } @if (!loading && invitation) {
  <section class="overview-grid">
    <article class="overview-card">
      <p class="overview-label">Status</p>
      <p class="overview-value">{{ statusLabel(invitation.status) }}</p>
    </article>
    <article class="overview-card">
      <p class="overview-label">Svarfrist</p>
      <p class="overview-value">
        {{ invitation.response_deadline | date : "short" }}
      </p>
    </article>
    <article class="overview-card">
      <p class="overview-label">Events</p>
      <p class="overview-value">{{ invitation.events.length }}</p>
    </article>
    <article class="overview-card">
      <p class="overview-label">Inviterede instruktører</p>
      <p class="overview-value">{{ invitation.invitees.length }}</p>
    </article>
  </section>
  <section class="section">
    <h3>Events i invitationen</h3>
    <ul class="event-list">
      @for (ev of invitation.events; track ev) {
      <li>
        <span class="event-title">{{ ev.event_name || ev.guid }}</span>
        <span class="event-meta"
          >{{ eventColumnLabel(ev) }}@if (ev.location) {
          <span> · {{ ev.location }}</span>
          }</span
        >
      </li>
      }
    </ul>
  </section>
  <section class="section">
    <h3>Inviterede instruktører</h3>
    <ul class="invitee-list">
      @for (u of invitation.invitees; track u) {
      <li class="invitee-row">
        <span>{{ inviteeName(u) }} ({{ u.email }})</span>
        @if (canEdit) {
        <button
          mat-button
          color="warn"
          type="button"
          (click)="removeInvitee(u)"
          [disabled]="removingInviteeId === u.user_id"
        >
          @if (removingInviteeId !== u.user_id) {
          <mat-icon>person_remove</mat-icon>
          } @if (removingInviteeId === u.user_id) {
          <mat-spinner diameter="18"></mat-spinner>
          } Fjern
        </button>
        }
      </li>
      }
    </ul>
    @if (canEdit) {
    <p class="section-label">Tilføj instruktør</p>
    <div class="search-container">
      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Søg på navn eller e-mail (mindst 2 tegn)</mat-label>
        <input
          matInput
          [ngModel]="searchQuery"
          (ngModelChange)="onSearchInput($event)"
          autocomplete="off"
          placeholder="fx. Jensen"
        />
        @if (!searching) {
        <mat-icon matSuffix>search</mat-icon>
        } @if (searching) {
        <mat-spinner matSuffix diameter="18"></mat-spinner>
        }
      </mat-form-field>
      @if (showSearchResults && searchResults.length > 0) {
      <div class="search-dropdown">
        @for (u of searchResults; track u) {
        <div class="search-result-item">
          <span class="result-info"
            >{{ displaySearchName(u) }} ({{ u.email }})</span
          >
          <button
            mat-button
            type="button"
            (click)="addInvitee(u)"
            [disabled]="addingInvitee"
          >
            Tilføj
          </button>
        </div>
        }
      </div>
      } @if (showSearchResults && !searching && searchResults.length === 0) {
      <p class="no-results">Ingen resultater eller allerede inviteret.</p>
      }
    </div>
    }
  </section>
  <section class="section">
    <h3>Svar fra instruktører</h3>
    <p class="hint">Viser hvem der har markeret et event som ønsket.</p>
    <div class="table-wrap matrix-scroll">
      <table class="matrix-table matrix-table--sticky">
        <thead>
          <tr>
            <th class="col-instructor">Instruktør</th>
            @for (ev of invitation.events; track ev) {
            <th
              class="event-col-header"
              [attr.title]="eventColumnLabel(ev)"
            >
              <span class="event-col-date">{{ eventColumnDateLine(ev) }}</span>
              <span class="event-col-time">{{ eventColumnTimeLine(ev) }}</span>
            </th>
            }
          </tr>
        </thead>
        <tbody>
          @for (u of invitation.invitees; track u) {
          <tr>
            <td class="col-instructor" [attr.title]="inviteeName(u)">
              {{ inviteeName(u) }}
            </td>
            @for (ev of invitation.events; track ev) {
            <td class="event-cell">
              @if (responded(ev.event_id, u.user_id)) {
              <span class="wanted" title="Ønsket">✓</span>
              } @if (!responded(ev.event_id, u.user_id)) {
              <span class="muted">–</span>
              }
            </td>
            }
          </tr>
          }
        </tbody>
      </table>
    </div>
  </section>
  @if (canEdit) {
  <section class="section">
    <h3>Fordeling af instruktører</h3>
    <div class="distribution-toolbar">
      <label for="distributionMode" class="distribution-label"
        >Fordel automatisk efter</label
      >
      <select
        id="distributionMode"
        [(ngModel)]="distributionMode"
        class="distribution-select"
      >
        <option value="events">Lige mange events pr. instruktør</option>
        <option value="hours">Lige mange timer pr. instruktør</option>
      </select>
      <button
        mat-raised-button
        type="button"
        (click)="distribute()"
        [disabled]="distributing"
      >
        {{ distributing ? "Fordeler…" : "Fordel automatisk" }}
      </button>
    </div>
    <p class="hint">
      Automatisk fordeling kan justeres manuelt nedenfor, og gemmes med "Gem
      fordeling".
    </p>
    <div class="table-wrap matrix-scroll">
      <table class="matrix-table matrix-table--sticky">
        <thead>
          <tr>
            <th class="col-instructor">Instruktør · tid</th>
            @for (ev of invitation.events; track ev) {
            <th
              class="event-col-header"
              [attr.title]="eventColumnLabel(ev)"
            >
              <span class="event-col-date">{{ eventColumnDateLine(ev) }}</span>
              <span class="event-col-time">{{ eventColumnTimeLine(ev) }}</span>
            </th>
            }
          </tr>
        </thead>
        <tbody>
          @for (u of invitation.invitees; track u) {
          <tr>
            <td class="col-instructor">
              <span class="assignee-inline" [attr.title]="inviteeName(u)">
                <span class="assignee-name">{{ inviteeName(u) }}</span>
                <span class="assignee-time">{{
                  assignedDurationLabelForInvitee(u.user_id)
                }}</span>
              </span>
            </td>
            @for (ev of invitation.events; track ev) {
            <td class="event-cell">
              <input
                type="checkbox"
                class="assign-checkbox"
                [checked]="isAssigned(ev.guid, u.user_id)"
                (change)="toggleAssignment(ev.guid, u.user_id)"
              />
            </td>
            }
          </tr>
          }
        </tbody>
      </table>
    </div>
  </section>
  } @if (actionError) {
  <p class="error">{{ actionError }}</p>
  } }
</mat-dialog-content>
@if (!loading && invitation && canEdit) {
<mat-dialog-actions align="end">
  <button mat-button (click)="close()">Luk</button>
  <button
    mat-button
    color="warn"
    (click)="deleteInvitation()"
    [disabled]="deleting"
  >
    Slet invitation
  </button>
  <button
    mat-raised-button
    (click)="saveAssignments()"
    [disabled]="savingAssignments"
  >
    Gem fordeling
  </button>
  <button
    mat-raised-button
    color="primary"
    (click)="complete()"
    [disabled]="completing"
  >
    Afslut og send mails
  </button>
</mat-dialog-actions>
} @if (!loading && invitation && !canEdit) {
<mat-dialog-actions align="end">
  <button mat-button (click)="close()">Luk</button>
  <button
    mat-button
    color="warn"
    (click)="deleteInvitation()"
    [disabled]="deleting || !canDelete"
  >
    Slet invitation
  </button>
</mat-dialog-actions>
}
